'use client';

import {Cookie} from '@config/cookies';
import {ClientCookies} from '@utils/clientCookies';
import {createProvidedStore} from '@utils/createProvidedStore';
import createZustandStore from '@utils/createZustandStore';

export type IServerCookies = [Cookie, {name: Cookie; value: string}][];
type ICookie = {name: Cookie; value: string};
interface ICookieStore {
    cookies: Record<Cookie, ICookie>;
    set: (name: Cookie, value: string) => void;
    hydrateSet: (name: Cookie, value: string) => void;
    delete: (name: Cookie) => void;
}

const getCookieValue = (value: string) => value.split(';')[0];

const convertServerCookies = (
    serverCookies: IServerCookies,
): Record<string, ICookie> => {
    return Object.fromEntries(
        serverCookies.map(([key, value]) => [
            key,
            {name: value.name, value: getCookieValue(value.value)},
        ]),
    );
};

const createCookieStore = (initialCookies: {cookies: IServerCookies}) =>
    createZustandStore<ICookieStore>((set, get) => ({
        cookies: convertServerCookies(initialCookies.cookies),
        set: (name, value) => {
            get().hydrateSet(name, value);
            ClientCookies.set(name, value);
        },
        // need this so that the client cookies don't duplicate
        hydrateSet: (name, value) => {
            set(state => ({
                cookies: {
                    ...state.cookies,
                    [name]: {name, value: getCookieValue(value)},
                },
            }));
        },
        delete: name => {
            ClientCookies.delete(name);
            set(state => {
                const {[name]: _, ...rest} = state.cookies;
                return {cookies: rest};
            });
        },
    }));

const store = createProvidedStore(createCookieStore);
export const CookieStoreProvider = store.StoreProvider;
export const useCookies = store.useStore;
